import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { ErrorBoundary } from './components/Layout/ErrorBoundary';
import { logger } from './services/logger';
import { env, isDev } from './config';
import './styles/globals.css';

// Initialize Sentry for error tracking (only if DSN is configured)
if (env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_APP_ENV,
    release: `invoice-agent@${env.VITE_APP_VERSION}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: isDev() ? 1.0 : 0.2,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  logger.info('Sentry initialized', { environment: env.VITE_APP_ENV });
}

// Global error handler for uncaught exceptions
window.addEventListener('error', (event) => {
  logger.error('Runtime Exception:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

// Global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled Promise Rejection:', {
    reason: event.reason?.message || event.reason,
  });
});

// Mount the application
const rootElement = document.getElementById('root');

if (!rootElement) {
  const errorMsg = "Critical: 'root' element missing. Application cannot mount.";
  console.error(errorMsg);
  Sentry.captureMessage(errorMsg, 'fatal');
} else {
  try {
    const root = createRoot(rootElement);

    // Wrap with Sentry error boundary if configured
    const AppWithErrorHandling = env.VITE_SENTRY_DSN
      ? Sentry.withErrorBoundary(App, {
          fallback: ({ error }) => (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
              <div className="text-center p-8">
                <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  {(error as Error)?.message || 'Unknown error'}
                </p>
                <button onClick={() => window.location.reload()} className="btn-primary">
                  Reload Page
                </button>
              </div>
            </div>
          ),
          showDialog: true,
        })
      : App;

    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <AppWithErrorHandling />
        </ErrorBoundary>
      </React.StrictMode>
    );

    logger.info('Application successfully mounted.', {
      version: env.VITE_APP_VERSION,
      environment: env.VITE_APP_ENV,
    });
  } catch (err) {
    logger.error('Bootstrap Failure:', err);
    Sentry.captureException(err);
    rootElement.innerHTML = `
      <div style="padding: 40px; text-align: center; font-family: sans-serif;">
        <h1 style="color: #ef4444;">Application Failed to Load</h1>
        <p>Please try refreshing the page. If the problem persists, clear your browser cache.</p>
        <button onclick="window.location.reload()" style="margin-top: 20px; padding: 10px 20px; cursor: pointer;">
          Reload
        </button>
      </div>
    `;
  }
}
